import React, { useLayoutEffect, useRef, useState } from 'react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@onward-delivery/design';
import { ArrowLeftRight, Check, CircleSlash, Clock, Icon } from '@onward-delivery/design/icons';
import { SETTING_ACTION_BUTTON, STATUS_ACCENT, STATUS_LABEL } from '../constants';

// Raw hex: the library has no `success` token, and #f4fdf7 and #fff8f8 have no stock Tailwind name.
const SURFACE = {
    met: 'border-[#86efac] bg-[#f4fdf7]',
    pending: 'border-[#fcd34d] bg-[#fffbeb]',
    missing: 'border-[#fca5a5] bg-[#fff8f8]',
};

const UNMET_STATUS_ICONS = { integration: ArrowLeftRight };

const DETAIL_TEXT = 'text-[13px] leading-[1.4] text-slate-500';
// Tailwind gap-1, which separates the lead, list, "+N" and trail.
const GAP_PX = 4;

// Shows as many items as fit on the line and folds the rest into "+N"; the title keeps the full list.
const OverflowDetail = ({ lead, items, trail, title }) => {
    const rowRef = useRef(null);
    const measureRef = useRef(null);
    const [shownCount, setShownCount] = useState(items.length);

    useLayoutEffect(() => {
        const fitItems = () => {
            const widthOf = (part) => measureRef.current.querySelector(`[data-part="${part}"]`)?.offsetWidth || 0;
            const itemWidths = items.map((item, index) => widthOf(`item-${index}`));
            const separatorWidth = widthOf('separator');
            const leadWidth = lead ? widthOf('lead') + GAP_PX : 0;
            const trailWidth = trail ? widthOf('trail') + GAP_PX : 0;
            const available = rowRef.current.clientWidth - leadWidth - trailWidth;
            let fitting = items.length;
            let used = itemWidths.reduce((sum, width) => sum + width, 0) + separatorWidth * (items.length - 1);
            while (fitting > 1 && used > available) {
                fitting -= 1;
                used -= itemWidths[fitting] + separatorWidth;
                if (fitting === items.length - 1) used += widthOf('more') + GAP_PX;
            }
            setShownCount(fitting);
        };

        fitItems();
        const observer = new ResizeObserver(fitItems);
        observer.observe(rowRef.current);

        return () => observer.disconnect();
    }, [lead, items, trail]);

    const hiddenCount = items.length - shownCount;

    return (
        <span
            ref={rowRef}
            title={title}
            className={`relative flex min-w-0 flex-1 gap-1 whitespace-nowrap ${DETAIL_TEXT}`}
        >
            <span ref={measureRef} aria-hidden="true" className="invisible absolute left-0 top-0 flex gap-1">
                {lead ? <span data-part="lead">{lead}</span> : null}
                {items.map((item, index) => (
                    <span key={item} data-part={`item-${index}`}>
                        {item}
                    </span>
                ))}
                <span data-part="separator">,&nbsp;</span>
                <span data-part="more">+{items.length}</span>
                {trail ? <span data-part="trail">{trail}</span> : null}
            </span>
            {lead ? <span className="shrink-0">{lead}</span> : null}
            <span className="min-w-0 truncate">{items.slice(0, shownCount).join(', ')}</span>
            {hiddenCount > 0 ? <span className="shrink-0 font-semibold text-slate-700">+{hiddenCount}</span> : null}
            {trail ? <span className="shrink-0">{trail}</span> : null}
        </span>
    );
};

const SettingCard = ({ card, icon, shippers = [], onAction, children }) => {
    let cardStatus = 'missing';
    if (card.pending) cardStatus = 'pending';
    if (card.satisfied) cardStatus = 'met';
    let statusIcon = UNMET_STATUS_ICONS[card.key] || CircleSlash;
    if (card.pending) statusIcon = Clock;
    if (card.satisfied) statusIcon = Check;
    const asksWhichShipper = shippers.length > 1;
    const singleShipperAction = (
        <button type="button" className={SETTING_ACTION_BUTTON} onClick={() => onAction(shippers[0])}>
            {card.actionLabel}
        </button>
    );
    const pickShipperAction = (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <button type="button" className={SETTING_ACTION_BUTTON}>
                    {card.actionLabel}
                </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
                {shippers.map((shipper) => (
                    <DropdownMenuItem key={shipper.client_id} onSelect={() => onAction(shipper)}>
                        {shipper.business_name}
                    </DropdownMenuItem>
                ))}
            </DropdownMenuContent>
        </DropdownMenu>
    );
    let actionButton = null;
    if (card.actionLabel) actionButton = asksWhichShipper ? pickShipperAction : singleShipperAction;

    const detailText = card.detailItems?.length ? (
        <OverflowDetail lead={card.detailLead} items={card.detailItems} trail={card.detailTrail} title={card.detail} />
    ) : (
        <span title={card.detail} className={`min-w-0 flex-1 truncate ${DETAIL_TEXT}`}>
            {card.detail}
        </span>
    );

    // A card with its own body, like the compliance badges, shows status per item instead.
    const statusBadge = children ? null : (
        <span className={`${STATUS_LABEL} ${STATUS_ACCENT[cardStatus]}`}>
            <Icon icon={statusIcon} size="sm" className="size-3 shrink-0" />
            {card.statusLabel}
        </span>
    );

    const body = children ?? (
        <div className="flex w-full items-center gap-2.5">
            {detailText}
            {actionButton}
        </div>
    );

    return (
        <div className={`flex min-w-0 flex-1 flex-col rounded-xl border px-4 py-2 ${SURFACE[cardStatus]}`}>
            <div className="flex w-full flex-col gap-2">
                <div className="flex w-full items-center justify-between gap-2">
                    <span className={`flex items-center gap-2 text-[15px] font-bold ${STATUS_ACCENT[cardStatus]}`}>
                        {icon ? <Icon icon={icon} size="sm" className="size-4 shrink-0" /> : null}
                        {card.title}
                    </span>
                    {statusBadge}
                </div>
                {body}
            </div>
        </div>
    );
};

export default SettingCard;
